import type { Security } from '@ipfabric/utils';
import { maybeProp, prop } from '@ipfabric/utils/fp/prop';
import type { SecuritySchemaMergedState } from 'components/SecuritySchema/model/SecuritySchemaModel';
import type { RuleChainScope } from 'components/SecuritySchema/model/SecuritySchemaScope';
import { array, option, ord, record } from 'fp-ts';
import { ordString } from 'fp-ts/lib/Ord';
import { flow } from 'fp-ts/lib/function';
import { createSelector } from 'reselect';
import { createScopedSelector } from 'utils/react/scopedSelector';

export interface SecurityEntityItem {
  name: string;
  scope: RuleChainScope;
}

const sortSecurityEntityItems = array.sort(
  ord.fromCompare<SecurityEntityItem>((a, b) =>
    ordString.compare(a.name.toLowerCase(), b.name.toLowerCase()),
  ),
);

const getVisibleRuleChainItemsOfType = (
  securityType: Security.SecurityType,
): ((machine?: Security.SecurityMachine | null) => SecurityEntityItem[]) =>
  flow(
    maybeProp('ruleChains'),
    option.fold(
      () => array.empty,
      flow(
        record.filter((ruleChain) => !ruleChain.hideInUi),
        record.collect((ruleChainName) => ({
          name: ruleChainName,
          scope: {
            name: ruleChainName,
            securityType,
            type: 'ruleChain',
          } as const,
        })),
        sortSecurityEntityItems,
      ),
    ),
  );

export const selectVisibleRuleChainItems: (
  state: SecuritySchemaMergedState,
) => { [Key in Security.SecurityType]: SecurityEntityItem[] } =
  createScopedSelector(() =>
    createSelector(prop('deviceSecurity'), (security) => ({
      acl: getVisibleRuleChainItemsOfType('acl')(security?.machineAcl),
      cloud: getVisibleRuleChainItemsOfType('cloud')(security?.machineCloud),
      nat44: getVisibleRuleChainItemsOfType('nat44')(security?.machineNat44),
      pbr: getVisibleRuleChainItemsOfType('pbr')(security?.machinePbr),
      zoneFw: getVisibleRuleChainItemsOfType('zoneFw')(security?.machineZones),
    })),
  );
